import { PageContainer } from '@ant-design/pro-components';
import { useRequest, history } from '@umijs/max';
import { Card, Tag, Button, Typography, Spin, Empty, Alert, Segmented, theme } from 'antd';
import { CheckCircleFilled, InfoCircleFilled, CalendarOutlined } from '@ant-design/icons';
import React from 'react';
import { getPlans } from '@/services/payment';
import { createStyles } from 'antd-style';
import { normalizePlanList, flattenPlanTabs, normalizeCycleKey, type CurrentSubscriptionSummary } from '@/pages/payment/utils';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';
import PlanBannerBg from '@/assets/icons/plan-banner-bg.png';

const { Title, Text } = Typography;

const PLAN_CORNER_ICON_MAP: Record<string, string> = {
  thunder: require('@/assets/icons/plan-icon-basic.png'),
  thunder_standard: require('@/assets/icons/plan-icon-standard.png'),
  thunder_enterprise: require('@/assets/icons/plan-icon-enterprise.png'),
};

const getPlanCornerIcon = (plan: { displayIcon?: string; planCode?: string }) => {
  if (!plan.displayIcon) return null;
  const code = String(plan.planCode || '').toLowerCase();
  if (code.includes('enterprise')) return PLAN_CORNER_ICON_MAP.thunder_enterprise;
  if (code.includes('standard') || code === 'pro') return PLAN_CORNER_ICON_MAP.thunder_standard;
  return PLAN_CORNER_ICON_MAP.thunder;
};

const useStyles = createStyles(({ token }) => ({
  page: {
    minHeight: 'calc(100vh - 56px)',
    background: token.colorBgLayout,
  },
  pageShell: {
    width: '100%',
    maxWidth: 1280,
    margin: '0 auto',
  },
  hero: {
    padding: 0,
    marginBottom: 0,
    borderRadius: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  currentSubscriptionSummary: {
    marginInline: 'auto',
    maxWidth: 640,
    width: '100%',
    padding: '12px 24px',
    borderRadius: 16,
    background: `linear-gradient(135deg, #0042da 0%, #3b5ee8 25%, #7c4dff 70%, #b44dff 100%)`,
    backgroundImage: `url(${PlanBannerBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: 'none',
    boxShadow: '0 8px 24px -10px rgba(59, 94, 232, 0.4)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentSubscriptionSummaryHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: 'rgba(255,255,255,0.9)',
    fontSize: 13,
    fontWeight: 600,
  },
  currentSubscriptionSummaryTitle: {
    display: 'inline',
    fontSize: 18,
    fontWeight: 800,
    color: '#fff',
    lineHeight: 1.3,
  },
  currentSubscriptionSummaryDesc: {
    display: 'inline',
    fontSize: 14,
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 500,
  },
  currentSubscriptionSummaryInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'nowrap',
  },
  currentSubscriptionSummaryDivider: {
    width: 1.5,
    height: 16,
    background: 'rgba(255,255,255,0.4)',
    flexShrink: 0,
    marginInline: 4,
  },
  tabToggleWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '24px 0 16px',
  },
  tabToggle: {
    padding: 4,
    borderRadius: 12,
    backgroundColor: '#f1f5f9',
    border: 'none',
    '.ant-segmented-item': {
      borderRadius: 10,
      fontWeight: 600,
      color: '#64748b',
      transition: 'all 0.2s',
      padding: '4px 16px',
    },
    '.ant-segmented-item-selected': {
      background: '#5c67f2',
      color: '#fff !important',
      boxShadow: '0 4px 12px rgba(92, 103, 242, 0.35)',
    },
    '.ant-segmented-item:hover:not(.ant-segmented-item-selected)': {
      color: '#334155',
    }
  },
  plansGrid: {
    display: 'flex',
    gap: 16,
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: '8px 0 24px 0',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '@media (max-width: 900px)': {
      flexWrap: 'wrap',
    },
  },
  planCard: {
    position: 'relative',
    borderRadius: 20,
    width: 240,
    flexShrink: 0,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid #e2e8f0`,
    backgroundColor: '#fff',
    boxShadow: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '.ant-card-body': {
      padding: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px -12px rgba(0, 0, 0, 0.1)',
      borderColor: '#5c67f2',
    },
  },
  promoCard: {
    borderColor: '#d946ef',
    '&:hover': {
      borderColor: '#d946ef',
      boxShadow: '0 12px 24px -12px rgba(217, 70, 239, 0.2)',
    },
  },
  selectedCard: {
    borderColor: '#5c67f2',
    borderWidth: 2,
    boxShadow: '0 12px 24px -12px rgba(92, 103, 242, 0.2)',
  },
  currentPlanBadge: {
    display: 'none',
  },
  planCornerIcon: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 20,
    height: 20,
    zIndex: 1,
  },
  planCardBody: {
    width: '100%',
    height: '100%',
    padding: '24px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  planHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  planIntroRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  planIntroTag: {
    borderRadius: 6,
    paddingInline: 8,
    paddingBlock: 2,
    marginInlineEnd: 0,
    border: 'none',
    background: '#e0e7ff',
    color: '#4f46e5',
    fontWeight: 700,
    fontSize: 11,
  },
  priceContainer: {
    textAlign: 'center',
    padding: '32px 16px',
    background: '#f8fafc',
    borderRadius: 16,
    margin: '8px 0',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  price: {
    fontSize: 48,
    fontWeight: 900,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  currency: {
    fontSize: 20,
    marginRight: 4,
    fontWeight: 600,
    alignSelf: 'center',
  },
  cycle: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 4,
    fontWeight: 500,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    margin: '8px 0',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    color: '#475569',
    fontWeight: 500,
  },
  checkIcon: {
    color: '#22c55e',
    fontSize: 16,
  },
  planFooter: {
    marginTop: 'auto',
    paddingTop: 12,
  },
  subscribeButton: {
    height: 48,
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    border: 'none',
    background: '#0f172a',
    color: '#fff',
    transition: 'all 0.2s',
    '&:not(:disabled):hover': {
      background: '#1e293b',
      transform: 'scale(1.02)',
    },
    '&:disabled': {
      background: '#f1f5f9',
      color: '#94a3b8',
    },
  },
  subscribeButtonPromo: {
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
    '&:not(:disabled):hover': {
      background: 'linear-gradient(135deg, #ff7b7b 0%, #ff9e63 100%)',
    },
  },
}));

type Plan = ReturnType<typeof flattenPlanTabs>[number];

const getBillingCycleLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'PERMANENT') return '';
  if (key === 'MONTHLY') return t('pages.payment.plans.billingCycle.month', 'month');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingCycle.year', 'year');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingCycle.twoWeek', '2 weeks');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingCycle.quarter', 'quarter');
  return billingCycle || t('pages.payment.plans.billingCycle.generic', 'cycle');
};

const getBillingCycleTabLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'MONTHLY') return t('pages.payment.plans.billingTab.month', 'Monthly');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingTab.year', 'Yearly');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingTab.twoWeek', 'Promo');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingTab.quarter', 'Quarterly');
  if (key === 'SPECIAL_OFFER') return t('pages.payment.plans.billingTab.specialOffer', 'Special');
  return billingCycle || t('pages.payment.plans.billingTab.generic', 'Plans');
};

const isFreePrice = (price: number | string) => {
  const numericPrice = Number(price || 0);
  return !Number.isFinite(numericPrice) || numericPrice <= 0;
};

const formatPriceValue = (price: number | string) => {
  const numericPrice = Number(price || 0);
  if (!Number.isFinite(numericPrice)) {
    return '0';
  }
  if (numericPrice <= 0) {
    return t('pages.payment.plans.free', 'Free');
  }
  return numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2);
};

const formatQuota = (quota: number | string) => {
  const n = Number(quota || 0);
  return (Number.isFinite(n) ? n : 0).toFixed(0);
};

const getRolloverText = (plan: Plan) => {
  if (plan.rolloverPolicy === 'FULL') {
    return t('pages.payment.plans.rollover.full', 'Full quota rollover');
  }
  if (plan.rolloverPolicy === 'RATIO' && plan.rolloverRatio != null) {
    return t('pages.payment.plans.rollover.ratio', '{ratio}% quota rollover', {
      ratio: Math.round(Number(plan.rolloverRatio) * 100),
    });
  }
  return t('pages.payment.plans.rollover.none', 'No quota rollover');
};

const getPlanSubtitle = (plan: Plan) => {
  const custom = String(plan.displaySubtitle || '').trim();
  if (custom) return custom;

  const code = String(plan.planCode || '').toLowerCase();
  if (code === 'free') return t('pages.payment.plans.subtitle.free', 'Best for first-time exploration');
  if (code === 'special_offer') return t('pages.payment.plans.subtitle.specialOffer', 'Great for short trials and quick onboarding');
  if (code.includes('basic')) return t('pages.payment.plans.subtitle.basic', 'Best for light daily use');
  if (code.includes('standard') || code === 'pro') return t('pages.payment.plans.subtitle.standard', 'Best for stable high-frequency use');
  if (code.includes('enterprise')) return t('pages.payment.plans.subtitle.enterprise', 'Best for teams and larger quotas');
  return t('pages.payment.plans.subtitle.default', 'Choose based on your current usage scale');
};

const getDisplayBadge = (plan: Plan) => {
  const custom = String(plan.displayBadge || '').trim();
  if (custom) return custom;
  const cycle = normalizeCycleKey(plan.billingCycle);
  if (cycle === 'TWO_WEEK') return t('pages.payment.plans.badge.twoWeek', 'Limited-time trial');
  if (cycle === 'YEARLY') return t('pages.payment.plans.badge.year', 'Better value for long-term use');
  return '';
};

const getPlanFeatures = (plan: Plan) => {
  if (Array.isArray(plan.displayFeatures) && plan.displayFeatures.length > 0) {
    return plan.displayFeatures.filter(Boolean).slice(0, 4);
  }

  const features = [
    t('pages.payment.plans.feature.credits', 'Includes {credits} credits', {
      credits: formatQuota(plan.creditQuota),
    }),
    t('pages.payment.plans.feature.duration', 'Valid for {days} days', {
      days: Number(plan.durationDays || 0) || 0,
    }),
    getRolloverText(plan),
  ];
  return features.filter(Boolean).slice(0, 4);
};

const formatSubscriptionExpiry = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const buildSubscriptionSummary = (currentSub?: CurrentSubscriptionSummary) => {
  const daysRemaining = Number(currentSub?.daysRemaining);
  const hasSubscription = currentSub?.hasSubscription === true
    || Boolean(currentSub?.planName)
    || Boolean(currentSub?.planCode)
    || Boolean(currentSub?.currentPeriodEnd)
    || Number.isFinite(daysRemaining);

  if (!hasSubscription) {
    return null;
  }

  // daysRemaining 为 null/undefined 且无到期时间 → 永久订阅
  const isPermanent = currentSub?.daysRemaining == null && !currentSub?.currentPeriodEnd;

  const safeDays = Math.max(0, Number.isFinite(daysRemaining) ? daysRemaining : 0);
  const planName = String(
    currentSub?.planName
    || currentSub?.planCode
    || t('pages.payment.plans.summary.currentPlan', 'Current Plan'),
  );
  const expiryText = formatSubscriptionExpiry(currentSub?.currentPeriodEnd);

  if (isPermanent) {
    return {
      title: planName,
      description: t('pages.payment.plans.summary.permanent', 'Permanently active'),
    };
  }

  const remainingLabel = Number.isFinite(daysRemaining)
    ? t('pages.payment.plans.summary.remaining', '{days} days remaining', { days: safeDays })
    : '';
  const remainingText = remainingLabel
    ? `${getAppLocale() === 'zh-CN' ? '（' : ' ('}${remainingLabel}${getAppLocale() === 'zh-CN' ? '）' : ')'}`
    : '';

  return {
    title: planName,
    description: expiryText
      ? t('pages.payment.plans.summary.validUntil', 'Active until {date}{remaining}', {
          date: expiryText,
          remaining: remainingText,
        })
      : remainingLabel,
  };
};

type StartCheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

const Plans: React.FC<{
  embedded?: boolean;
  onStartCheckout?: (ctx: StartCheckoutContext) => void;
}> = ({ embedded, onStartCheckout }) => {
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const {
    data: planList,
    loading,
    error,
  } = useRequest(getPlans, {
    formatResult: normalizePlanList,
    cacheKey: 'payment-plans',
    staleTime: 15_000,
    onError: (e) => {
      console.error('Failed to load plans:', e);
    },
  });
  const tabs = React.useMemo(() => Array.isArray(planList?.tabs) ? planList.tabs : [], [planList?.tabs]);
  const plans = React.useMemo(() => flattenPlanTabs(tabs), [tabs]);
  const currentSub = React.useMemo(
    () => planList?.currentSubscription,
    [planList?.currentSubscription],
  );
  const currentSubscriptionSummary = React.useMemo(
    () => buildSubscriptionSummary(currentSub),
    [currentSub],
  );
  const [activeTabKey, setActiveTabKey] = React.useState<string>('');

  React.useEffect(() => {
    if (tabs.length === 0) return;
    if (tabs.some((tab) => tab.tabKey === activeTabKey)) return;
    const defaultTabKey = String(planList?.defaultTabKey || '');
    const matched = tabs.find((tab) => tab.tabKey === defaultTabKey);
    setActiveTabKey(matched?.tabKey || tabs[0].tabKey);
  }, [tabs, activeTabKey, planList?.defaultTabKey]);

  const [selectedPlanCode, setSelectedPlanCode] = React.useState<string>('');
  const activeTab = React.useMemo(
    () => tabs.find((tab) => tab.tabKey === activeTabKey) || tabs[0],
    [tabs, activeTabKey],
  );
  const cyclePlans = React.useMemo(() => activeTab?.plans || [], [activeTab]);

  React.useEffect(() => {
    const exists = cyclePlans.some(
      (p) => String(p?.planCode || '').toLowerCase() === String(selectedPlanCode || '').toLowerCase(),
    );
    if (!exists && selectedPlanCode) {
      setSelectedPlanCode('');
    }
  }, [selectedPlanCode, cyclePlans]);

  const handleSubscribe = (plan: Plan) => {
    if (embedded && onStartCheckout) {
      onStartCheckout({
        orderType: 'SUBSCRIPTION',
        planCode: String(plan.planCode || ''),
        planName: String(plan.planName || ''),
        billingCycle: String(plan.billingCycle || ''),
        durationDays: Number(plan.durationDays || 0) || undefined,
      });
      return;
    }

    const params = new URLSearchParams();
    params.set('orderType', 'SUBSCRIPTION');
    params.set('planCode', String(plan.planCode || ''));
    params.set('billingCycle', String(plan.billingCycle || ''));
    history.push(`/payment/checkout?${params.toString()}`);
  };

  if (loading) {
    if (embedded) {
      return (
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <PageContainer
        className={styles.page}
        ghost={false}
        title={t('pages.payment.plans.title', 'Choose a subscription plan')}
        subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
      >
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  const content = (
    <div className={styles.pageShell} style={{ padding: embedded ? 0 : '12px 0 0' }}>
        <div className={styles.hero}>
          {embedded && (
            <Title level={3} style={{ textAlign: 'center', marginBottom: 24, fontWeight: 800 }}>
              {t('pages.payment.plans.title', 'Choose a subscription plan')}
            </Title>
          )}
          {currentSubscriptionSummary && (
            <div className={styles.currentSubscriptionSummary}>
              <div className={styles.currentSubscriptionSummaryInner}>
                <span className={styles.currentSubscriptionSummaryHeader}>
                  <CalendarOutlined />
                  <span>{t('pages.payment.plans.summary.active', 'Current active subscription')}</span>
                </span>
                <span className={styles.currentSubscriptionSummaryTitle}>
                  {currentSubscriptionSummary.title}
                </span>
                {currentSubscriptionSummary.description && (
                  <>
                    <span className={styles.currentSubscriptionSummaryDivider} />
                    <span className={styles.currentSubscriptionSummaryDesc}>
                      {currentSubscriptionSummary.description}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message={t('pages.payment.plans.loadFailed', 'Failed to load subscription plans')}
            description={(error as any)?.message || t('pages.payment.plans.loadFailedDesc', 'Please refresh and try again later, or ask an administrator to check the payment plan configuration.')}
            style={{ marginBottom: 24 }}
          />
        )}

        {!error && plans.length === 0 && (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
            />
          </Card>
        )}

        {!error && plans.length > 0 && (
          <>
            {tabs.length > 1 && (
              <div className={styles.tabToggleWrap}>
                <Segmented
                  size="large"
                  className={styles.tabToggle}
                  value={activeTab?.tabKey}
                  onChange={(v) => setActiveTabKey(String(v))}
                  options={tabs.map((tab) => ({
                    label: tab.tabName || getBillingCycleTabLabel(tab.tabKey),
                    value: tab.tabKey,
                  }))}
                />
              </div>
            )}

            {cyclePlans.length === 0 ? (
              <Card>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={activeTab?.tabName ? t('pages.payment.plans.emptyGroup', 'No {group} plans are available right now', { group: activeTab.tabName }) : t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
                />
              </Card>
            ) : (
              <div className={styles.plansGrid}>
                {cyclePlans.map((plan) => {
                  const purchaseLimit = Number((plan as any).purchaseLimit);
                  const isSelected =
                    String(selectedPlanCode || '').toLowerCase() === String(plan.planCode || '').toLowerCase();
                  const isCurrentVariant = Boolean(plan.current);
                  const actionDisabled = Boolean(plan.actionDisabled);
                  const actionLabel = String(
                    plan.actionLabel ||
                      (isCurrentVariant
                        ? t('pages.payment.plans.currentSubscription', 'Current Plan')
                        : actionDisabled
                          ? t('pages.payment.plans.cta.subscribed', 'Subscribed to another plan')
                          : isFreePrice(plan.price)
                            ? t('pages.payment.plans.cta.tryNow', 'Try Now')
                            : t('pages.payment.plans.cta.subscribeNow', 'Subscribe Now')),
                  );
                  const cornerIcon = getPlanCornerIcon(plan);
                  const isPromo = normalizeCycleKey(plan.billingCycle) === 'TWO_WEEK';

                return (
                  <Card
                    key={`${activeTab?.tabKey || 'tab'}-${plan.id}`}
                    className={`${styles.planCard} ${isPromo ? styles.promoCard : ''} ${isSelected && !isPromo ? styles.selectedCard : ''}`}
                    onClick={() => {
                      setSelectedPlanCode(plan.planCode);
                    }}
                    hoverable
                    role="button"
                    aria-pressed={isSelected}
                  >
                    {isCurrentVariant && (
                      <div className={styles.currentPlanBadge}>
                        <CheckCircleFilled />
                        {t('pages.payment.plans.currentSubscription', 'Current Plan')}
                      </div>
                    )}
                    {cornerIcon && !isCurrentVariant && (
                      <img src={cornerIcon} alt="" className={styles.planCornerIcon} />
                    )}

                    <div className={styles.planCardBody}>
                      <div className={styles.planHeader}>
                        <div className={styles.planIntroRow}>
                          <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
                            {plan.planName}
                          </Title>
                          {getDisplayBadge(plan) && (
                            <Tag
                              className={styles.planIntroTag}
                              style={
                                isPromo
                                  ? { background: '#fff1f2', color: '#f43f5e' }
                                  : undefined
                              }
                            >
                              {getDisplayBadge(plan)}
                            </Tag>
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500, marginTop: 4, color: '#64748b' }}>
                          {getPlanSubtitle(plan)}
                        </Text>
                      </div>

                      <div className={styles.priceContainer}>
                        <div className={styles.price}>
                          {isFreePrice(plan.price) ? (
                            <span style={{ fontSize: 40, fontWeight: 900 }}>{t('pages.payment.plans.free', 'Free')}</span>
                          ) : (
                            <>
                              <span className={styles.currency}>¥</span>
                              <span style={{ fontSize: 48, letterSpacing: '-1px' }}>{formatPriceValue(plan.price)}</span>
                            </>
                          )}
                          {getBillingCycleLabel(plan.billingCycle) && (
                            <span className={styles.cycle}>/ {getBillingCycleLabel(plan.billingCycle)}</span>
                          )}
                        </div>
                      </div>

                      <div className={styles.featureList}>
                        {getPlanFeatures(plan).map((feature, index) => (
                          <div className={styles.featureItem} key={`${plan.id}-feature-${index}`}>
                            <CheckCircleFilled className={styles.checkIcon} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                        {Number.isFinite(purchaseLimit) && purchaseLimit > 0 && (
                          <div className={styles.featureItem}>
                            <InfoCircleFilled style={{ color: token.colorWarning, fontSize: 15, marginTop: 1 }} />
                            <Text type="warning">{t('pages.payment.plans.purchaseLimit', 'Purchase limit: {count} per user', { count: purchaseLimit })}</Text>
                          </div>
                        )}
                      </div>

                      <div className={styles.planFooter}>
                        <Button
                          type="default"
                          block
                          className={`${styles.subscribeButton} ${isPromo ? styles.subscribeButtonPromo : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlanCode(plan.planCode);
                            handleSubscribe(plan);
                          }}
                          disabled={actionDisabled || isCurrentVariant}
                        >
                          {actionLabel}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
                })}
              </div>
            )}

          </>
        )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <PageContainer
      className={styles.page}
      ghost={false}
      title={t('pages.payment.plans.title', 'Choose a subscription plan')}
      subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
    >
      {content}
    </PageContainer>
  );
};

export default Plans;
